import React, { useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ConditionalTooltip } from '@atlassian/conversation-assistant-ui-components/ui/ConditionalTooltip';
import { LoadingState } from '@atlassian/conversation-assistant-ui-components/ui/LoadingState';

const styles = cssMap({
	menuItem: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		minHeight: '32px',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.075'),
		borderRadius: token('radius.medium'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingInline: token('space.150'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	menuItemActive: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
	menuItemDisabled: {
		cursor: 'not-allowed',
		color: token('color.text.disabled'),
		opacity: token('opacity.disabled'),
	},
	menuItemContent: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		flex: '1',
		minWidth: '0px',
	},
	menuItemIcon: {
		display: 'flex',
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	menuItemIconAdjusted: {
		display: 'flex',
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
		color: token('color.text.subtle'),
	},
	menuItemLabelContainer: {
		flex: '1',
		minWidth: '0px',
		textAlign: 'left',
	},
	menuItemLabel: {
		display: 'block',
		width: '100%',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		overflow: 'hidden',
	},
});

interface MenuItemProps {
	label: string;
	icon: React.ReactNode;
	onClick: () => void;
	isActive?: boolean; // specific to agent menu items
	isLoading?: boolean;
	isDisabled?: boolean;
	testId?: string;
}

export const MenuItem = ({
	label,
	icon,
	onClick,
	isActive,
	isLoading,
	isDisabled,
	testId,
}: MenuItemProps): React.JSX.Element => {
	const [showTooltip, setShowTooltip] = useState(false);
	const textRef = useRef<HTMLElement>(null);
	const containerRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const checkOverflow = () => {
			if (textRef.current && containerRef.current) {
				const isOverflowing = textRef.current.scrollWidth > containerRef.current.clientWidth;
				setShowTooltip(isOverflowing);
			}
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => resizeObserver.disconnect();
	}, [label]);

	return (
		<Pressable
			xcss={cx(
				styles.menuItem,
				isActive && styles.menuItemActive,
				isDisabled && styles.menuItemDisabled,
			)}
			onClick={isDisabled ? undefined : onClick}
			aria-disabled={isDisabled}
			role="menuitem"
			testId={testId || 'menu-item-container'}
		>
			<Box xcss={styles.menuItemContent} testId="menu-item-content">
				<Box
					xcss={
						fg('rovo_chat_refresh_design_adjustments')
							? styles.menuItemIconAdjusted
							: styles.menuItemIcon
					}
				>
					{isLoading ? <LoadingState size="small" testId="loading-state" /> : icon}
				</Box>
				<Box xcss={styles.menuItemLabelContainer} ref={containerRef}>
					<ConditionalTooltip withTooltip={showTooltip} content={label}>
						<Box xcss={styles.menuItemLabel} ref={textRef}>
							<Text
								color={isActive ? 'color.text.selected' : 'color.text.subtle'}
								testId="menu-item-label"
							>
								{label}
							</Text>
						</Box>
					</ConditionalTooltip>
				</Box>
			</Box>
			{isActive && <CheckMarkIcon label="checkmark" />}
		</Pressable>
	);
};
